import {isNonEmpty} from 'web-app/util/non-empty-array';
import {needsTranslations} from 'web-app/util/scoped-needs-translation';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import * as DateFormatter from '../helpers/date-formatter';
import {filterPricingGroupsBySelectedPricingGroup, lowestAndHighestPrice} from '../helpers/helpers';
import type * as Types from '../types';
import {AUTOMATIC_PRICING_GROUP_ID} from '../constants';

const transformBillingPeriodsToBillingPeriodOptions = (
    billingPeriods: null | Types.BillingPeriods,
    customPeriodState: Types.State['customPeriodState'],
): Array<Types.BillingPeriodOption> => {
    if (!hasValue(billingPeriods)) {
        return [];
    }

    const customPeriod: Types.CustomPeriodOption = {
        type: 'custom',
        label: needsTranslations('SimplifiedUsBilling', 'Custom period'),
        value: customPeriodState.customPeriodDates
            ? DateFormatter.numericFormat(customPeriodState.customPeriodDates.startDate)
            : 'customPeriod',
        from: customPeriodState.customPeriodDates
            ? DateFormatter.numericFormat(customPeriodState.customPeriodDates.startDate)
            : null,
        to: customPeriodState.customPeriodDates
            ? DateFormatter.numericFormat(customPeriodState.customPeriodDates.endDate)
            : null,
    };

    switch (billingPeriods.type) {
        case 'monthly': {
            return billingPeriods.periods.map<Types.BillingPeriodOption>(period => {
                switch (period.type) {
                    case 'custom':
                        return customPeriod;
                    default:
                        return {
                            type: billingPeriods.type,
                            label: `${period.month} ${period.year}`,
                            value: period.startDate.numericFormat,
                            from: period.startDate.textFormat,
                            to: period.endDate.textFormat,
                        };
                }
            });
        }
        case 'weekly': {
            return billingPeriods.periods.map(period => {
                switch (period.type) {
                    case 'custom':
                        return customPeriod;
                    default:
                        return {
                            type: billingPeriods.type,
                            label: `${period.startDate.textFormat} - ${period.endDate.textFormat} ${period.year}`,
                            value: period.startDate.numericFormat,
                        };
                }
            });
        }
        case 'biweekly': {
            return billingPeriods.periods.map(period => {
                switch (period.type) {
                    case 'custom':
                        return customPeriod;
                    default:
                        return {
                            type: billingPeriods.type,
                            label: `${period.startDate.textFormat} - ${period.endDate.textFormat} ${period.year}`,
                            value: period.startDate.numericFormat,
                        };
                }
            });
        }
        default:
            return [];
    }
};

const transformTuitionOrFeeToTuitionAndFeeOption = ({
    tuitionOrFee,
    selectedPricingGroup,
    monthlyPeriodPricingGroups,
    weeklyPeriodPricingGroups,
}: {
    tuitionOrFee: Types.Tuition | Types.Fee;
    selectedPricingGroup: Types.State['pricingGroupState']['selectedPricingGroup'];
    monthlyPeriodPricingGroups: null | Array<Types.PeriodPricingGroup>;
    weeklyPeriodPricingGroups: null | Array<Types.PeriodPricingGroup>;
}): Types.TuitionAndFeeOption | null => {
    switch (tuitionOrFee.type) {
        case 'monthly': {
            const validatedTuitionOrFee = validateTuitionOrFee({
                tuitionOrFee,
                selectedPricingGroupId: selectedPricingGroup,
                periodPricingGroups: monthlyPeriodPricingGroups,
            });

            if (!hasValue(validatedTuitionOrFee)) {
                return null;
            }

            return {
                type: 'monthly',
                label: tuitionOrFee.title,
                value: tuitionOrFee.id,
                lowestAndHighestPrice: lowestAndHighestPrice(
                    validatedTuitionOrFee.pricingGroups.map(({price}) => price),
                ),
                frequency: 'month',
            };
        }
        case 'weekly': {
            const validatedTuitionOrFee = validateTuitionOrFee({
                tuitionOrFee,
                selectedPricingGroupId: selectedPricingGroup,
                periodPricingGroups: weeklyPeriodPricingGroups,
            });

            if (!hasValue(validatedTuitionOrFee)) {
                return null;
            }

            return {
                type: 'weekly',
                label: tuitionOrFee.title,
                value: tuitionOrFee.id,
                lowestAndHighestPrice: lowestAndHighestPrice(
                    validatedTuitionOrFee.pricingGroups.map(({price}) => price),
                ),
                frequency: 'week',
            };
        }
        default:
            return null;
    }
};

const transformChildrenToChildrenOptions = (children: Array<Types.Child>): Array<Types.ChildrenOption> => {
    return children.map(child => {
        return {
            label: child.fullName,
            value: child.id,
            imageUrl: child.profileImageUrl,
        };
    });
};

const transformDiscountToDiscountOption = (discount: Types.Discount): Types.DiscountOption => {
    return {
        label: discount.title,
        value: discount.id,
        amount:
            discount.type === 'custom' && discount.customDiscountType === 'amount'
                ? `$ ${discount.amount}`
                : `${Math.round(discount.amount * 100)}%`,
        type: 'discount',
    };
};

const hasAllPeriodPricingGroups = (
    pricingGroups: Array<Types.PricingGroup>,
    periodPricingGroups: Array<Types.PeriodPricingGroup>,
): boolean => {
    return periodPricingGroups.every(periodPricingGroup =>
        pricingGroups.some(pricingGroup => pricingGroup.id === periodPricingGroup.pricingGroupId),
    );
};

const validateTuitionOrFee = ({
    tuitionOrFee,
    periodPricingGroups,
    selectedPricingGroupId,
}: {
    tuitionOrFee: Types.Tuition | Types.Fee;
    periodPricingGroups: null | Array<Types.PeriodPricingGroup>;
    selectedPricingGroupId: Types.State['pricingGroupState']['selectedPricingGroup'];
}) => {
    if (selectedPricingGroupId === AUTOMATIC_PRICING_GROUP_ID) {
        if (!periodPricingGroups) {
            return null;
        }
        if (!hasAllPeriodPricingGroups(tuitionOrFee.pricingGroups, periodPricingGroups)) {
            return null;
        }

        if (periodPricingGroups.length !== tuitionOrFee.pricingGroups.length) {
            const pricingGroups = filterPricingGroupsBySelectedPricingGroup({
                selectedPricingGroup: selectedPricingGroupId,
                allPricingGroups: tuitionOrFee.pricingGroups,
                periodPricingGroups,
            }).filter(pricingGroup => !isNaN(pricingGroup.price));

            if (!isNonEmpty(pricingGroups)) {
                return null;
            }
            return {...tuitionOrFee, pricingGroups};
        }

        return tuitionOrFee;
    }

    const pricingGroups = filterPricingGroupsBySelectedPricingGroup({
        selectedPricingGroup: selectedPricingGroupId,
        allPricingGroups: tuitionOrFee.pricingGroups,
        periodPricingGroups,
    }).filter(pricingGroup => !isNaN(pricingGroup.price));

    if (!isNonEmpty(pricingGroups)) {
        return null;
    }
    return {...tuitionOrFee, pricingGroups};
};

export {
    transformBillingPeriodsToBillingPeriodOptions,
    transformTuitionOrFeeToTuitionAndFeeOption,
    transformChildrenToChildrenOptions,
    transformDiscountToDiscountOption,
    validateTuitionOrFee,
};
